import Card from '../../../components/base/Card';
import { useResumenes } from '../../../shared/hooks/useResumenes';
import { formatCurrency } from '../../../shared/utils/currency-utils';
import { PDFUploader } from '../../../components/finance';

export default function ResumenesSection() {
  const {
    resumenSeleccionado,
    tendencias,
    insightsConsolidados,
    navegacion,
    irMesAnterior,
    irMesSiguiente,
    irMesActual,
    datosComparativos
  } = useResumenes();

  const getVariacionColor = (variacion: number) => {
    if (variacion > 0) return 'text-emerald-600';
    if (variacion < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getVariacionIcon = (variacion: number) => {
    if (variacion > 0) return 'ri-arrow-up-line';
    if (variacion < 0) return 'ri-arrow-down-line';
    return 'ri-subtract-line';
  };

  const getTendenciaColor = (tendencia: string) => {
    switch (tendencia) {
      case 'positiva': return 'text-emerald-600';
      case 'negativa': return 'text-red-600';
      default: return 'text-yellow-600';
    }
  };

  const getTendenciaIcon = (tendencia: string) => {
    switch (tendencia) {
      case 'positiva': return 'ri-trending-up-line';
      case 'negativa': return 'ri-trending-down-line';
      default: return 'ri-subtract-line';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header con navegación de meses */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Resúmenes Financieros</h1>
          <p className="text-gray-600 mt-1">Análisis detallado de tu situación financiera</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <button
            onClick={irMesAnterior}
            disabled={!navegacion.puedeRetroceder}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          
          <div className="text-center min-w-[150px]">
            <h2 className="text-lg font-semibold text-gray-900">{navegacion.tituloMes}</h2>
            {!navegacion.esMesActual && (
              <button 
                onClick={irMesActual}
                className="text-sm text-emerald-600 hover:text-emerald-700"
              >
                Ir al mes actual
              </button>
            )}
          </div>
          
          <button
            onClick={irMesSiguiente}
            disabled={!navegacion.puedeAvanzar}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>

      {/* Resumen principal del mes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Ingresos</p>
              <p className="text-2xl font-bold text-emerald-600">
                {formatCurrency(resumenSeleccionado.totalIngresos)}
              </p>
              {resumenSeleccionado.comparacionMesAnterior.ingresos.valor !== 0 && (
                <p className={`text-sm flex items-center ${getVariacionColor(resumenSeleccionado.comparacionMesAnterior.ingresos.valor)}`}>
                  <i className={`${getVariacionIcon(resumenSeleccionado.comparacionMesAnterior.ingresos.valor)} mr-1`}></i>
                  {Math.abs(resumenSeleccionado.comparacionMesAnterior.ingresos.porcentaje).toFixed(1)}%
                </p>
              )}
            </div>
            <div className="p-3 bg-emerald-100 rounded-full">
              <i className="ri-arrow-up-line text-2xl text-emerald-600"></i>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Egresos</p>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(resumenSeleccionado.totalEgresos)}
              </p>
              {resumenSeleccionado.comparacionMesAnterior.egresos.valor !== 0 && (
                <p className={`text-sm flex items-center ${getVariacionColor(-resumenSeleccionado.comparacionMesAnterior.egresos.valor)}`}>
                  <i className={`${getVariacionIcon(resumenSeleccionado.comparacionMesAnterior.egresos.valor)} mr-1`}></i>
                  {Math.abs(resumenSeleccionado.comparacionMesAnterior.egresos.porcentaje).toFixed(1)}%
                </p>
              )}
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <i className="ri-arrow-down-line text-2xl text-red-600"></i>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Balance</p>
              <p className={`text-2xl font-bold ${resumenSeleccionado.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(resumenSeleccionado.balance)}
              </p>
              <p className="text-sm text-gray-500">
                Ahorro: {resumenSeleccionado.porcentajeAhorro.toFixed(1)}%
              </p>
            </div>
            <div className={`p-3 rounded-full ${resumenSeleccionado.balance >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <i className={`ri-scales-line text-2xl ${resumenSeleccionado.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}></i>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Patrimonio</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(resumenSeleccionado.patrimonioNeto)}
              </p>
              {resumenSeleccionado.variacionPatrimonio !== 0 && (
                <p className={`text-sm flex items-center ${getVariacionColor(resumenSeleccionado.variacionPatrimonio)}`}>
                  <i className={`${getVariacionIcon(resumenSeleccionado.variacionPatrimonio)} mr-1`}></i>
                  {formatCurrency(Math.abs(resumenSeleccionado.variacionPatrimonio))}
                </p>
              )}
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <i className="ri-safe-line text-2xl text-blue-600"></i>
            </div>
          </div>
        </Card>
      </div>

      {/* Desglose por categorías */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Ingresos por Categoría</h3>
          <div className="space-y-3">
            {Object.entries(resumenSeleccionado.ingresosPorCategoria)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 5)
              .map(([categoria, monto]) => {
                const porcentaje = (monto / resumenSeleccionado.totalIngresos) * 100;
                return (
                  <div key={categoria} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 capitalize">{categoria}</span>
                        <span className="text-sm text-gray-600">{formatCurrency(monto)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-emerald-500 h-2 rounded-full" 
                          style={{ width: `${porcentaje}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Egresos por Categoría</h3>
          <div className="space-y-3">
            {Object.entries(resumenSeleccionado.egresosPorCategoria)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 5)
              .map(([categoria, monto]) => {
                const porcentaje = (monto / resumenSeleccionado.totalEgresos) * 100;
                return (
                  <div key={categoria} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 capitalize">{categoria}</span>
                        <span className="text-sm text-gray-600">{formatCurrency(monto)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ width: `${porcentaje}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>
      </div>

      {/* Métricas adicionales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Métricas Diarias</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Promedio ingresos/día:</span>
              <span className="font-medium">{formatCurrency(resumenSeleccionado.ingresoPromedioDiario)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Promedio gastos/día:</span>
              <span className="font-medium">{formatCurrency(resumenSeleccionado.gastoPromedioDiario)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Días con ingresos:</span>
              <span className="font-medium">{resumenSeleccionado.diasConIngresos}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Días con gastos:</span>
              <span className="font-medium">{resumenSeleccionado.diasConGastos}</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tendencias</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Ingresos:</span>
              <div className={`flex items-center ${getTendenciaColor(tendencias.tendenciaIngresos)}`}>
                <i className={`${getTendenciaIcon(tendencias.tendenciaIngresos)} mr-1`}></i>
                <span className="capitalize font-medium">{tendencias.tendenciaIngresos}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Egresos:</span>
              <div className={`flex items-center ${getTendenciaColor(tendencias.tendenciaEgresos)}`}>
                <i className={`${getTendenciaIcon(tendencias.tendenciaEgresos)} mr-1`}></i>
                <span className="capitalize font-medium">{tendencias.tendenciaEgresos}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Balance:</span>
              <div className={`flex items-center ${getTendenciaColor(tendencias.tendenciaBalance)}`}>
                <i className={`${getTendenciaIcon(tendencias.tendenciaBalance)} mr-1`}></i>
                <span className="capitalize font-medium">{tendencias.tendenciaBalance}</span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Promedio ahorro:</span>
              <span className="font-medium">{tendencias.promedioAhorro.toFixed(1)}%</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Inversiones</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total invertido:</span>
              <span className="font-medium">{formatCurrency(resumenSeleccionado.totalInversiones)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Rendimiento mes:</span>
              <span className={`font-medium ${resumenSeleccionado.rendimientoMensual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(resumenSeleccionado.rendimientoMensual)}
              </span>
            </div>
            {Object.entries(resumenSeleccionado.inversionesPorTipo).length > 0 && (
              <div className="space-y-2">
                <span className="text-sm text-gray-600">Por tipo:</span>
                {Object.entries(resumenSeleccionado.inversionesPorTipo)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 3)
                  .map(([tipo, monto]) => (
                    <div key={tipo} className="flex justify-between text-sm">
                      <span className="text-gray-600 capitalize">{tipo}:</span>
                      <span>{formatCurrency(monto)}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Insights y Recomendaciones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Insights del Mes</h3>
          <div className="space-y-3">
            {resumenSeleccionado.insights.map((insight, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="p-1 bg-blue-100 rounded-full mt-1">
                  <i className="ri-lightbulb-line text-blue-600 text-sm"></i>
                </div>
                <p className="text-gray-700 text-sm">{insight}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recomendaciones</h3>
          <div className="space-y-3">
            {resumenSeleccionado.recomendaciones.map((recomendacion, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="p-1 bg-emerald-100 rounded-full mt-1">
                  <i className="ri-check-line text-emerald-600 text-sm"></i>
                </div>
                <p className="text-gray-700 text-sm">{recomendacion}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Insights consolidados */}
      {insightsConsolidados.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Análisis General</h3>
          <div className="space-y-3">
            {insightsConsolidados.map((insight, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="p-1 bg-yellow-100 rounded-full mt-1">
                  <i className="ri-star-line text-yellow-600 text-sm"></i>
                </div>
                <p className="text-gray-700 text-sm">{insight}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Gráfico de evolución (simplificado) */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Evolución Últimos Meses</h3>
        <div className="space-y-4">
          {datosComparativos.slice(-6).map((dato, index) => (
            <div key={index} className="flex items-center space-x-4">
              <div className="w-16 text-sm text-gray-600">{dato.mes}</div>
              <div className="flex-1 grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Ingresos: </span>
                  <span className="text-emerald-600 font-medium">{formatCurrency(dato.ingresos)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Egresos: </span>
                  <span className="text-red-600 font-medium">{formatCurrency(dato.egresos)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Balance: </span>
                  <span className={`font-medium ${dato.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatCurrency(dato.balance)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Sección de procesamiento de PDFs */}
      <div className="mt-8">
        <PDFUploader />
      </div>
    </div>
  );
}